import { injectable } from "inversify";
import { Message, Field, Type } from "protobufjs/light";
import { Result, ok } from "@xlearn/common";
import * as Api from "@/app/state-serialization/api";
import { ProtobufSerializer } from "@/app/state-serialization/infrastructure";
import { FillInAnswer, FillInAnswers, FillInAnswerResults } from "./fill-in-interfaces";

interface FillInAnswerItem {
  key: string;
  val: string;
}

interface FillInAnswerResultItem {
  key: string;
  val: boolean;
}

@injectable()
export class FillInQuestionAnswerProtobufSerializer
  implements Api.StateSerializationPlugin<FillInAnswer> {
  key = {
    id: 12,
    name: { ns: "suspend-data", name: "question-answer-fill-in" },
    format: "protobuf"
  };

  serialize(data: FillInAnswer): Result<Uint8Array, Api.SerializerError> {
    const answers: FillInAnswerItem[] = [];
    const answerResults: FillInAnswerResultItem[] = [];

    for (let i in data.answers) {
      answers.push({
        key: i,
        val: data.answers[i],
      })
    }

    for (let i in data.answerResults) {
      answerResults.push({
        key: i,
        val: data.answerResults[i],
      })
    }

    const message = new FillInAnswerProtoMessage({
      answers,
      answerResults,
    });

    return ProtobufSerializer.serialize<FillInAnswerProtoMessage>(
      message,
      FillInAnswerProtoMessage
    );
  }

  deserialize(
    content: Uint8Array
  ): Result<FillInAnswer, Api.DeserializerError> {
    const deserializeResult = ProtobufSerializer.deserialize<
      FillInAnswerProtoMessage
    >(content, FillInAnswerProtoMessage);
    if (!deserializeResult.success) {
      return deserializeResult;
    }

    const message = deserializeResult.value;
    const answers: FillInAnswers = {};
    const answerResults: FillInAnswerResults = {};
    message.answers.forEach(answer => answers[answer.key] = answer.val);
    message.answerResults.forEach(answerResult => answerResults[answerResult.key] = answerResult.val);
    
    const result: FillInAnswer = {
      answers,
      answerResults,
    };

    return ok(result);
  }
}

@Type.d("FillInAnswerItemProtoMessage")
class FillInAnswerItemProtoMessage extends Message<FillInAnswerItemProtoMessage> {
  @Field.d(1, "string", "optional", "")
  key: string;

  @Field.d(2, "string", "optional", "")
  val: string;
}

@Type.d("FillInAnswerResultItemProtoMessage")
class FillInAnswerResultItemProtoMessage extends Message<FillInAnswerResultItemProtoMessage> {
  @Field.d(1, "string", "optional", "")
  key: string;

  @Field.d(2, "bool", "optional", false)
  val: boolean;
}

@Type.d("FillInAnswerProtoMessage")
class FillInAnswerProtoMessage extends Message<FillInAnswerProtoMessage> {
  @Field.d(1, FillInAnswerItemProtoMessage, "repeated")
  answers: FillInAnswerItem[];

  @Field.d(2, FillInAnswerResultItemProtoMessage, "repeated")
  answerResults: FillInAnswerResultItem[];
}
